import {
  STATION_PIN_HEIGHT_PX,
  STATION_PIN_TIP_Y,
  STATION_PIN_WIDTH_PX,
} from "@ryde/shared";
import { redMui } from "@ryde/theme";

/**
 * MapLibre-only pin settings. The shape itself — size, tip and outline path — is shared with the Leaflet
 * pin as `STATION_PIN_*` in `@ryde/shared`, so both renderers place a station identically.
 */
export const MAPLIBRE_STATION_PIN_VIEW_BOX = `0 0 ${STATION_PIN_WIDTH_PX} ${STATION_PIN_HEIGHT_PX}`;

/** Outline drawn on the DropOff pin, which is white-filled and needs an edge against the map. */
export const MAPLIBRE_STATION_PIN_OUTLINE_WIDTH_PX = 1.5;

/**
 * How far the number badge overhangs the top of the pin body — the pin's tallest point is the badge, not
 * the pin. Shared with `MapLibreStationPinRoot`, which positions the badge, so the two cannot drift.
 */
export const MAPLIBRE_STATION_PIN_BADGE_TOP_OVERSHOOT_PX = 2;

/** Breathing room between the tallest part of the pin and the popup's tail. */
const STATION_PIN_POPUP_GAP_PX = 4;

/**
 * Lift (px) for a popup opened from a station pin.
 *
 * The pin is tip-anchored — its body is drawn ABOVE the coordinate — so a popup at MapLibre's default
 * offset of 0 puts its tail on the tip and buries the whole pin. Derived from the pin's own geometry rather
 * than tuned by eye: the tip sits `STATION_PIN_TIP_Y` below the body's top, and a DropOff pin's badge
 * overhangs that top by another 2px, so anything less than ~41px still lands the tail on the badge.
 *
 * This is deliberately a little more than Leaflet's rendered lift (~32px, which left the tail overlapping
 * the pin's crown and, on badged pins, the badge itself).
 */
export const MAPLIBRE_STATION_PIN_POPUP_OFFSET_PX = Math.ceil(
  STATION_PIN_TIP_Y +
    MAPLIBRE_STATION_PIN_BADGE_TOP_OVERSHOOT_PX +
    STATION_PIN_POPUP_GAP_PX,
);

/** Centre of the pin head, where the waypoint number sits. */
export const MAPLIBRE_STATION_PIN_LABEL_X = 14.25;
export const MAPLIBRE_STATION_PIN_LABEL_Y = 18;
export const MAPLIBRE_STATION_PIN_LABEL_FONT_SIZE = 13;
export const MAPLIBRE_STATION_PIN_LABEL_FONT_WEIGHT = 700;

/**
 * The pin dropped on an address that has been picked but is not yet a station: a red teardrop with a pale
 * disc in its head.
 *
 * It is the app's own marker, already used for the passengers map's search result, and it now also stands
 * in wherever a Google map used to render its default red pin — the address picker and the landing pages'
 * station chooser. Those pins were an SDK asset and left with it.
 */
export const MAPLIBRE_PICKED_ADDRESS_PIN_COLOR = redMui.A400;

/** The disc inside that pin. Passed as the pin's label, exactly as the Leaflet icon did. */
export const MAPLIBRE_PICKED_ADDRESS_PIN_GLYPH = "⚪";
